// SEQ-182 T10: the pure arithmetic behind the P&L tab's operational hours
// band (PnlManualMetricRows.tsx / useProjectManualMetrics.ts). Split into
// its own module, with no React and no supabase import, so it Deno-tests in
// isolation exactly like pnlCalculationCore.ts and rollup.ts already do.
//
// THE ONE RULE THIS FILE ENFORCES: the Total column's total labour hours is
// always ordinary-hours-total plus overtime-hours-total, recomputed from the
// summed components, never a third value summed on its own (plan section
// 6.1: "calculated.total_labour_hours is recomputed from the summed
// components rather than summed, so the same rule holds at every grain").
//
// NULL VS ZERO: a month with no payroll data for a metric carries `null`,
// not 0 (RPC section 6.3: pnl_safe_div returns null on a zero denominator,
// and the matview simply has no row for an unmapped site or empty range).
// Summing across months treats a null month as "no contribution", exactly
// like the existing dollar rollups in rollup.ts. But if EVERY month is null
// for a metric, the total stays null too, so the band never renders a zero
// that reads as "no overtime this year" when the true answer is "no data".

export interface OperationalHoursMonth {
  /** ISO 'YYYY-MM-01', matching PnlAnalyticsRow.periodMonth. */
  periodMonth: string;
  ordinaryHours: number | null;
  overtimeHours: number | null;
  totalLabourHours: number | null;
}

export interface OperationalHoursTotals {
  ordinaryHours: number | null;
  overtimeHours: number | null;
  totalLabourHours: number | null;
}

function sumOrNull(values: Array<number | null>): number | null {
  const present = values.filter((v): v is number => v !== null);
  if (present.length === 0) return null;
  return present.reduce((a, b) => a + b, 0);
}

/**
 * Sums the Ordinary Hours and Overtime Hours columns across every month
 * client side (both aggregate as `sum`, plan section 6.1), then recomputes
 * Total Labour Hours from those two sums rather than summing the per-month
 * `totalLabourHours` values directly. Mathematically the two approaches
 * agree when every month has both components, but recomputing is the one
 * that stays correct when a month is missing one component and not the
 * other (a partially mapped site, or a mid-range payroll classification
 * change), which a naive sum-of-totals would silently get wrong.
 */
export function totalOperationalHours(months: OperationalHoursMonth[]): OperationalHoursTotals {
  const ordinaryHours = sumOrNull(months.map((m) => m.ordinaryHours));
  const overtimeHours = sumOrNull(months.map((m) => m.overtimeHours));
  return { ordinaryHours, overtimeHours, totalLabourHours: labourHoursTotal(ordinaryHours, overtimeHours) };
}

/**
 * Total Labour Hours from its two components, at any grain.
 *
 * Exported because the P&L tab now derives the row client side rather than
 * reading calculated.total_labour_hours back from the RPC. Since
 * 20260917000100 both components are typed by hand, and the RPC's copy is a
 * request behind the cell the user just edited; deriving it here means the
 * subtotal cannot disagree with the two rows above it on screen.
 *
 * One present component is enough: a month with ordinary hours typed and
 * overtime left blank totals to the ordinary hours, not to null. Both blank
 * stays null, so an untouched month never renders a 0 that reads as "nobody
 * worked".
 */
export function labourHoursTotal(ordinaryHours: number | null, overtimeHours: number | null): number | null {
  if (ordinaryHours === null && overtimeHours === null) return null;
  return (ordinaryHours ?? 0) + (overtimeHours ?? 0);
}

/** True when every month carries no value for any of the three metrics, i.e. the band has nothing to show. */
export function isOperationalHoursEmpty(months: OperationalHoursMonth[]): boolean {
  return months.every((m) => m.ordinaryHours === null && m.overtimeHours === null && m.totalLabourHours === null);
}
